import Dialog from '@rio-cloud/rio-uikit/Dialog';
import ListMenu from '@rio-cloud/rio-uikit/ListMenu';
import { type ReactNode, useEffect, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate, useSearchParams } from 'react-router';

import { FeatureToggles } from '@/configuration/featureToggle/featureToggles';
import { UserManualNavItem } from '@/features/userManual/components/UserManualNavItem';
import { UserManualPages } from '@/features/userManual/components/UserManualPages';
import {
    findChargingStationsManualPages,
    pagesForFunctionSectionOfUserManualDialog,
    pagesForServiceSectionOfUserManualDialog,
    userManualFaq,
    userManualSections,
} from '@/features/userManual/utils';
import { useFeatureToggle } from '@/hooks/useFeatureToggle';
import { DialogType, useModal } from '@/hooks/useModal';
import { checkPermission, useUserStore } from '@/stores/userStore';

export const UserManualModal = (): ReactNode => {
    const navigate = useNavigate();
    const { account } = useUserStore();
    const [searchParams] = useSearchParams();
    const { openDialog, closeDialog } = useModal();
    const { value } = useFeatureToggle(FeatureToggles.ENABLE_ADDITIONAL_VAT);
    const isAdditionalVatEnabled = value as boolean;

    const userManualTab = searchParams.get('userManualTab');

    const pages = useMemo(
        () => [
            ...pagesForFunctionSectionOfUserManualDialog(isAdditionalVatEnabled),
            ...pagesForServiceSectionOfUserManualDialog,
            ...findChargingStationsManualPages,
        ],
        [isAdditionalVatEnabled]
    );

    const selectedPage = pages.find(p => p.keyPage === userManualTab) ?? null;

    useEffect(() => {
        if (!selectedPage) {
            closeDialog();
            return;
        }

        openDialog({
            type: DialogType.SPLIT,
            show: !!userManualTab,
            onClose: handleClose,
            title: <FormattedMessage id='userManual.modalTitle' />,
            bsSize: Dialog.SIZE_XL,
            leftContent: <ListMenu menuItems={menuItems} groupClassName='padding-left-0' />,
            leftContentClassName: 'width-20pct',
            rightContent: <UserManualPages {...selectedPage} />,
        });
    }, [openDialog, closeDialog, selectedPage]);

    const getFaqSubMenuItems = () =>
        userManualFaq.map(item => ({
            key: item.keyFaqPage,
            item: (
                <UserManualNavItem
                    keyPage={userManualSections.service.faq}
                    prefix='userManual.faq'
                    anchor={item.keyFaqPage}
                    section='faq'
                />
            ),
            isSubItem: true,
        }));

    const getServiceNavItems = () =>
        pagesForServiceSectionOfUserManualDialog.flatMap(({ keyPage }) => {
            if (keyPage === userManualSections.service.faq) {
                return [
                    {
                        key: keyPage,
                        item: <UserManualNavItem keyPage={keyPage} prefix='userManual.sections' section='faq' />,
                    },
                    ...(userManualTab === userManualSections.service.faq ? getFaqSubMenuItems() : []),
                ];
            }

            return [
                {
                    key: keyPage,
                    item: <UserManualNavItem keyPage={keyPage} prefix='userManual.sections' />,
                },
            ];
        });

    const getChargingStationsNavItems = () =>
        findChargingStationsManualPages.map(({ keyPage }) => ({
            key: keyPage,
            item: (
                <UserManualNavItem
                    keyPage={keyPage}
                    prefix='userManual.findChargingStations'
                    section='find_charging_stations'
                />
            ),
        }));

    const getFunctionNavItems = () =>
        pagesForFunctionSectionOfUserManualDialog(isAdditionalVatEnabled)
            .filter(({ permissions }) => !permissions || checkPermission(account, permissions))
            .map(({ keyPage }) => ({
                key: keyPage,
                item: <UserManualNavItem keyPage={keyPage} prefix='userManual' section='functions' />,
            }));

    const menuItems = [
        {
            group: <FormattedMessage id='userManual.menu.service.title' />,
            navItems: getServiceNavItems(),
        },
        {
            group: <FormattedMessage id='userManual.sections.findChargingStations.title' />,
            navItems: getChargingStationsNavItems(),
        },
        {
            group: <FormattedMessage id='userManual.menu.title' />,
            navItems: getFunctionNavItems(),
        },
    ];

    const handleClose = () => {
        navigate('?', { replace: true });
    };

    return null;
};
